/**
 * Strategy Detail Page
 *
 * Displays a single strategy with its drivers, linked objectives/goals, and cycles.
 * Also handles creation when strategyId is "new".
 * Part of Workstream 5: Strategy Chain
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  PencilIcon,
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  ArchiveBoxIcon,
  LinkIcon,
  XMarkIcon,
  FlagIcon,
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useStrategyStore } from '../../../stores/prioritization/strategyStore';
import { useStrategyCanvasStore } from '../../../stores/prioritization/strategyCanvasStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useOutcomeDriverStore, type OutcomeDriver } from '../../../stores/prioritization/outcomeDriverStore';
import {
  getStrategyStatusLabel,
  getTimeHorizonLabel,
  type StrategyTimeHorizon,
} from '../../../types/prioritization/strategy';
import {
  listObjectives,
  listGoals,
  createGoal,
  updateObjective,
  updateGoal,
  type StrategicObjective,
  type Goal,
} from '../../../lib/strategyAlignment';
import { createStrategicObjective as createObjective } from '../../../lib/strategy/createStrategyFlow';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { StrategyConnectivityHelpButton } from './StrategyConnectivityDocModal';
import { ObjectiveWeightsPanel } from './ObjectiveWeightsPanel';

// ============================================================================
// Linked Outcome Drivers panel
// ============================================================================

interface LinkedOutcomeDriversPanelProps {
  strategyId: string;
  linkedDrivers: OutcomeDriver[];
  allDrivers: OutcomeDriver[];
  onLink: (driverId: string) => void;
  onUnlink: (driverId: string) => void;
}

function LinkedOutcomeDriversPanel({
  strategyId: _strategyId,
  linkedDrivers,
  allDrivers,
  onLink,
  onUnlink,
}: LinkedOutcomeDriversPanelProps) {
  const [query, setQuery] = useState('');
  const [linking, setLinking] = useState<string | null>(null);

  const available = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    const linkedIds = new Set(linkedDrivers.map(d => d.id));
    return allDrivers
      .filter(d => !linkedIds.has(d.id))
      .filter(d => d.title?.toLowerCase().includes(q) || d.narrative?.toLowerCase().includes(q))
      .slice(0, 8);
  }, [allDrivers, linkedDrivers, query]);

  async function handleLink(driverId: string) {
    setLinking(driverId);
    await onLink(driverId);
    setQuery('');
    setLinking(null);
  }

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]/40 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--rule)]">
        <p className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wide">
          Outcome Drivers
        </p>
        <Link
          to="/prioritization/outcome-drivers"
          className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] flex items-center gap-1"
        >
          <PlusIcon className="h-3.5 w-3.5" /> New driver
        </Link>
      </div>

      {/* Linked drivers list */}
      {linkedDrivers.length > 0 && (
        <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
          {linkedDrivers.map(driver => (
            <div key={driver.id} className="flex items-start gap-2 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                  {driver.title || 'Untitled driver'}
                </p>
                {driver.narrative && (
                  <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5 line-clamp-2">
                    {driver.narrative}
                  </p>
                )}
              </div>
              <button
                onClick={() => onUnlink(driver.id)}
                className="flex-shrink-0 mt-0.5 p-1 rounded text-[var(--text-ter)] hover:text-[var(--coral)] dark:hover:text-[var(--coral)] transition-colors"
                title="Unlink from strategy"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Inline search to link */}
      <div className="px-4 py-3 border-t border-[var(--rule)]">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search outcome drivers to link…"
            className="w-full pl-8 pr-3 py-2 text-xs border border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg)]/40 text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-[var(--bg-raised)] dark:focus:bg-[var(--bg)]/60"
          />
        </div>

        {query.trim() && available.length === 0 && (
          <p className="mt-2 text-xs text-[var(--text-ter)] px-1">No matching unlinked drivers.</p>
        )}

        {available.length > 0 && (
          <div className="mt-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)] shadow-sm max-h-48 overflow-y-auto">
            {available.map(driver => (
              <button
                key={driver.id}
                onClick={() => handleLink(driver.id)}
                disabled={linking === driver.id}
                className="w-full text-left px-3 py-2.5 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-colors disabled:opacity-50"
              >
                <p className="text-xs font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                  {driver.title || 'Untitled driver'}
                </p>
                {driver.narrative && (
                  <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] truncate mt-0.5">
                    {driver.narrative}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}

        {linkedDrivers.length === 0 && !query && (
          <p className="mt-2 text-xs text-[var(--text-ter)]">
            Search above to link existing outcome drivers, or{' '}
            <Link to="/prioritization/outcome-drivers" className="text-[var(--text-ter)] hover:underline">
              create a new one
            </Link>
            .
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Linked Cycles panel
// ============================================================================

interface DegapCycle { id: string; name: string; currentPhase?: string; }

interface LinkedCyclesPanelProps {
  linkedCycleIds: string[];
  cycles: DegapCycle[];
  onLink: (cycleId: string) => void;
  onUnlink: (cycleId: string) => void;
}

function LinkedCyclesPanel({ linkedCycleIds, cycles, onLink, onUnlink }: LinkedCyclesPanelProps) {
  const [query, setQuery] = useState('');

  const available = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    const linkedSet = new Set(linkedCycleIds);
    return cycles
      .filter(c => !linkedSet.has(c.id) && c.name?.toLowerCase().includes(q))
      .slice(0, 8);
  }, [cycles, linkedCycleIds, query]);

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]/40 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--rule)]">
        <p className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wide">
          Linked Cycles
        </p>
      </div>

      {linkedCycleIds.length > 0 && (
        <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
          {linkedCycleIds.map(cycleId => {
            const cycle = cycles.find(c => c.id === cycleId);
            return (
              <div key={cycleId} className="flex items-center gap-2 px-4 py-3">
                <Link
                  to={`/prioritization/cycles/${cycleId}`}
                  className="flex-1 min-w-0 text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] truncate flex items-center gap-1.5"
                >
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">{cycle?.name || cycleId}</span>
                </Link>
                <button
                  onClick={() => onUnlink(cycleId)}
                  className="flex-shrink-0 p-1 rounded text-[var(--text-ter)] hover:text-[var(--coral)] dark:hover:text-[var(--coral)] transition-colors"
                  title="Unlink cycle"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="px-4 py-3 border-t border-[var(--rule)]">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search cycles to link…"
            className="w-full pl-8 pr-3 py-2 text-xs border border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg)]/40 text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-[var(--bg-raised)] dark:focus:bg-[var(--bg)]/60"
          />
        </div>

        {query.trim() && available.length === 0 && (
          <p className="mt-2 text-xs text-[var(--text-ter)] px-1">No matching unlinked cycles.</p>
        )}

        {available.length > 0 && (
          <div className="mt-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)] shadow-sm max-h-48 overflow-y-auto">
            {available.map(cycle => (
              <button
                key={cycle.id}
                onClick={() => { onLink(cycle.id); setQuery(''); }}
                className="w-full text-left px-3 py-2.5 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-colors"
              >
                <p className="text-xs font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{cycle.name}</p>
                {cycle.currentPhase && (
                  <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">Phase: {cycle.currentPhase}</p>
                )}
              </button>
            ))}
          </div>
        )}

        {linkedCycleIds.length === 0 && !query && (
          <p className="mt-2 text-xs text-[var(--text-ter)]">
            Search above to link planning cycles to this strategy.
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Creation form
// ============================================================================

type StrategyFramework = 'classic' | 'ost' | 'kernel';

const FRAMEWORK_OPTIONS: { id: StrategyFramework; label: string; tagline: string }[] = [
  { id: 'classic', label: 'Classic Strategy', tagline: 'Name, description, objectives, goals' },
  { id: 'ost', label: 'Opportunity Solution Tree', tagline: 'Outcome → opportunities → solutions' },
  { id: 'kernel', label: 'Kernel Strategy', tagline: 'Diagnosis → guiding policy → actions' },
];

function StrategyCreateForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createStrategy = useStrategyStore(state => state.createStrategy);
  const addNotification = useUIStore(state => state.addNotification);
  const userId = useUserStore(state => state.userId);

  const [framework, setFramework] = useState<StrategyFramework>('classic');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    timeHorizon: 'annual' as StrategyTimeHorizon,
  });

  useEffect(() => {
    const f = searchParams.get('framework');
    if (f === 'ost' || f === 'kernel' || f === 'classic') setFramework(f);
  }, [searchParams]);

  const frameworkParam = searchParams.get('framework');
  const frameworkFromUrl =
    frameworkParam === 'ost' || frameworkParam === 'kernel' || frameworkParam === 'classic';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Please enter a strategy name');
      return;
    }
    const created = await createStrategy({
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      timeHorizon: formData.timeHorizon,
      ownerId: userId,
    });
    if (!created.ok || !created.id) {
      // Leave the form filled in so the user can retry.
      addNotification({
        type: 'error',
        title: 'Strategy was not created',
        message: created.error ?? 'Try again.',
        duration: 6000,
      });
      return;
    }
    const strategyId = created.id;
    if (framework === 'ost') {
      navigate(`/prioritization/strategy/opportunity-trees/new?strategyId=${encodeURIComponent(strategyId)}`);
      return;
    }
    if (framework === 'kernel') {
      navigate('/prioritization/strategy/kernel/new', {
        state: {
          parentStrategyId: strategyId,
          prefillName: formData.name.trim(),
          prefillDescription: formData.description.trim(),
          prefillTimeHorizon: formData.timeHorizon,
        },
      });
      return;
    }
    navigate(`/prioritization/strategy/${strategyId}`);
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-3xl mx-auto">
      <div className="mb-6">
        <Link
          to="/prioritization/strategy/list"
          className="inline-flex items-center text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]"
        >
          <ArrowLeftIcon className="mr-1 h-4 w-4" />
          Back to Strategies
        </Link>
      </div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <h1 className="text-2xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Create Strategy</h1>
        <StrategyConnectivityHelpButton context="strategy_create" />
      </div>
      {!frameworkFromUrl ? (
        <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-6">
          Choose a framework — you define the strategy record here first; OST and Kernel open right after you create it.
        </p>
      ) : (
        <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-6">
          {framework === 'ost' && 'Opportunity Solution Tree — name and describe the strategy, then you’ll open the tree on that strategy.'}
          {framework === 'kernel' && 'Kernel strategy — name and describe the strategy record, then you’ll continue in the Rumelt kernel workspace.'}
          {framework === 'classic' && 'Classic strategy — name and describe the strategy, then you’ll open the strategy detail page.'}
        </p>
      )}

      {/* Framework picker — hidden when opened from Strategies hub with a pre-selected type */}
      {!frameworkFromUrl && (
        <div className="grid grid-cols-3 gap-3 mb-8">
          {FRAMEWORK_OPTIONS.map(opt => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setFramework(opt.id)}
              className={`text-left px-4 py-3  border-2 transition-all ${
                framework === opt.id
                  ? 'border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20'
                  : 'border-[var(--rule)] hover:border-[var(--rule)] dark:hover:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]/40'
              }`}
            >
              <p className={`text-sm font-semibold ${framework === opt.id ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]' : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'}`}>
                {opt.label}
              </p>
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5 leading-snug">{opt.tagline}</p>
            </button>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
            Strategy Name <span className="text-[var(--coral)]">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Q2 2026 Product Strategy"
            className="w-full px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-[var(--slate)]"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="What is this strategy for?"
            rows={2}
            className="w-full px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-[var(--slate)] resize-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Time Horizon</label>
          <select
            value={formData.timeHorizon}
            onChange={(e) => setFormData({ ...formData, timeHorizon: e.target.value as StrategyTimeHorizon })}
            className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-[var(--slate)]"
          >
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
            <option value="multi-year">Multi-Year</option>
          </select>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Link
            to="/prioritization/strategy/list"
            className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={!formData.name.trim()}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Create Strategy
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================================
// Linked Strategy Canvases panel
// ============================================================================

interface LinkedStrategyCanvasesPanelProps {
  strategyId: string;
}

function LinkedStrategyCanvasesPanel({ strategyId }: LinkedStrategyCanvasesPanelProps) {
  const canvases      = useStrategyCanvasStore((s) => s.canvases);
  const fetchCanvases = useStrategyCanvasStore((s) => s.fetchCanvases);
  const linkStrategy   = useStrategyCanvasStore((s) => s.linkStrategy);
  const unlinkStrategy = useStrategyCanvasStore((s) => s.unlinkStrategy);
  const [query, setQuery] = useState('');
  const [linking, setLinking] = useState<string | null>(null);

  useEffect(() => { fetchCanvases(); }, [fetchCanvases]);

  const linkedCanvases = useMemo(
    () => canvases.filter((c) => c.linkedStrategyIds.includes(strategyId)),
    [canvases, strategyId]
  );

  const linkedIds = useMemo(() => new Set(linkedCanvases.map((c) => c.id)), [linkedCanvases]);

  const suggestions = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    return canvases.filter((c) => !linkedIds.has(c.id) && c.title.toLowerCase().includes(q)).slice(0, 6);
  }, [canvases, linkedIds, query]);

  async function handleLink(canvasId: string) {
    setLinking(canvasId);
    await linkStrategy(canvasId, strategyId);
    setQuery('');
    setLinking(null);
  }

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]/40 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--rule)]">
        <p className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wide">
          Strategy Canvases
        </p>
        <Link
          to="/prioritization/strategy/canvas"
          className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] flex items-center gap-1"
        >
          <PlusIcon className="h-3.5 w-3.5" /> New canvas
        </Link>
      </div>

      {linkedCanvases.length > 0 && (
        <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
          {linkedCanvases.map((canvas) => (
            <div key={canvas.id} className="flex items-center gap-2 px-4 py-3">
              <Link
                to={`/prioritization/strategy/canvas/${canvas.id}`}
                className="flex-1 min-w-0 text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] truncate flex items-center gap-1.5"
              >
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{canvas.title}</span>
              </Link>
              <button
                onClick={() => unlinkStrategy(canvas.id, strategyId)}
                className="flex-shrink-0 p-1 rounded text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
                title="Unlink canvas"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="px-4 py-3 border-t border-[var(--rule)]">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search canvases to link…"
            className="w-full pl-8 pr-3 py-2 text-xs border border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg)]/40 text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-[var(--bg-raised)] dark:focus:bg-[var(--bg)]/60"
          />
        </div>

        {query.trim() && suggestions.length === 0 && (
          <p className="mt-2 text-xs text-[var(--text-ter)] px-1">No matching canvases found.</p>
        )}

        {suggestions.length > 0 && (
          <div className="mt-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)] shadow-sm max-h-48 overflow-y-auto">
            {suggestions.map((canvas) => (
              <button
                key={canvas.id}
                onClick={() => handleLink(canvas.id)}
                disabled={linking === canvas.id}
                className="w-full text-left px-3 py-2.5 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-colors disabled:opacity-50"
              >
                <p className="text-xs font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{canvas.title}</p>
              </button>
            ))}
          </div>
        )}

        {linkedCanvases.length === 0 && !query && (
          <p className="mt-2 text-xs text-[var(--text-ter)]">
            Search above to link a strategy canvas, or{' '}
            <Link to="/prioritization/strategy/canvas" className="text-[var(--text-ter)] hover:underline">
              create a new one
            </Link>
            .
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Priority badge helper
// ============================================================================

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-[var(--coral-dim)] text-[var(--coral)]',
  high: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  medium: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  low: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
};

function PriorityBadge({ priority }: { priority: string }) {
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${PRIORITY_COLORS[priority] ?? 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
      {priority}
    </span>
  );
}

// ============================================================================
// Detail page
// ============================================================================

export function StrategyDetail() {
  const { strategyId } = useParams();
  const navigate = useNavigate();

  const isCreating = strategyId === 'new';

  const strategiesMap = useStrategyStore(state => state.strategies);
  const updateStrategy = useStrategyStore(state => state.updateStrategy);
  const activateStrategy = useStrategyStore(state => state.activateStrategy);
  const archiveStrategy = useStrategyStore(state => state.archiveStrategy);
  const deleteStrategy = useStrategyStore(state => state.deleteStrategy);
  const linkCyclesToStrategy = useStrategyStore(state => state.linkCyclesToStrategy);
  const unlinkCycleFromStrategy = useStrategyStore(state => state.unlinkCycleFromStrategy);

  const cycles = useDegapProcessStore(state => state.cycles);
  const userId = useUserStore(state => state.userId);
  const workspaceId = useTenantStore(state => state.currentWorkspaceId) ?? getCurrentWorkspaceId() ?? '';

  const allDrivers = useOutcomeDriverStore(state => state.drivers);
  const fetchDrivers = useOutcomeDriverStore(state => state.fetchFromSupabase);
  const updateDriver = useOutcomeDriverStore(state => state.updateDriver);

  const strategy = useMemo(() => {
    if (isCreating || !strategiesMap || !strategyId) return null;
    if (strategiesMap instanceof Map) return strategiesMap.get(strategyId);
    return strategiesMap[strategyId];
  }, [strategiesMap, strategyId, isCreating]);

  const linkedDrivers = useMemo(
    () => allDrivers.filter(d => d.strategyId === strategyId),
    [allDrivers, strategyId]
  );

  // UI state
  const [isEditing, setIsEditing] = useState(false);
  const [showAddObjectiveModal, setShowAddObjectiveModal] = useState(false);
  const [showLinkObjectiveModal, setShowLinkObjectiveModal] = useState(false);
  const [showAddGoalModal, setShowAddGoalModal] = useState(false);
  const [showLinkGoalModal, setShowLinkGoalModal] = useState(false);

  // Edit form — mirrors strategy fields
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    timeHorizon: 'annual' as StrategyTimeHorizon,
  });
  const [isSaving, setIsSaving] = useState(false);

  // Sync edit form when strategy loads or edit mode opens
  useEffect(() => {
    if (strategy && isEditing) {
      setEditForm({
        name: strategy.name,
        description: strategy.description ?? '',
        timeHorizon: strategy.timeHorizon,
      });
    }
  }, [strategy, isEditing]);

  // Objectives & Goals — linked to this strategy
  const [objectives, setObjectives] = useState<StrategicObjective[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  // All workspace objectives/goals for the link picker
  const [allObjectives, setAllObjectives] = useState<StrategicObjective[]>([]);
  const [allGoals, setAllGoals] = useState<Goal[]>([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [linking, setLinking] = useState(false);

  const loadRelated = useCallback(async () => {
    if (!strategyId || !workspaceId) return;
    setLoadingRelated(true);
    try {
      const [objs, gls] = await Promise.all([
        listObjectives(workspaceId, strategyId),
        listGoals(workspaceId, undefined, strategyId),
      ]);
      setObjectives(objs);
      setGoals(gls);
    } finally {
      setLoadingRelated(false);
    }
  }, [strategyId, workspaceId]);

  useEffect(() => {
    if (!isCreating) {
      loadRelated();
      fetchDrivers();
    }
  }, [isCreating, loadRelated, fetchDrivers]);

  // Load all workspace objectives/goals when a link modal opens
  const openLinkObjectiveModal = useCallback(async () => {
    setShowLinkObjectiveModal(true);
    setLoadingAll(true);
    try {
      const all = await listObjectives(workspaceId);
      setAllObjectives(all);
    } finally {
      setLoadingAll(false);
    }
  }, [workspaceId]);

  const openLinkGoalModal = useCallback(async () => {
    setShowLinkGoalModal(true);
    setLoadingAll(true);
    try {
      const all = await listGoals(workspaceId);
      setAllGoals(all);
    } finally {
      setLoadingAll(false);
    }
  }, [workspaceId]);

  const handleLinkObjective = useCallback(async (objId: string) => {
    setLinking(true);
    try {
      await updateObjective(objId, { strategyId: strategy?.id });
      setShowLinkObjectiveModal(false);
      await loadRelated();
    } finally {
      setLinking(false);
    }
  }, [strategy?.id, loadRelated]);

  const handleLinkGoal = useCallback(async (goalId: string) => {
    setLinking(true);
    try {
      await updateGoal(goalId, { strategyId: strategy?.id });
      setShowLinkGoalModal(false);
      await loadRelated();
    } finally {
      setLinking(false);
    }
  }, [strategy?.id, loadRelated]);

  // Quick-add objective form
  const [newObjName, setNewObjName] = useState('');
  const [newObjDescription, setNewObjDescription] = useState('');
  const [newObjPriority, setNewObjPriority] = useState<'critical' | 'high' | 'medium' | 'low'>('medium');
  const [newObjTimeHorizon, setNewObjTimeHorizon] = useState<'quarterly' | 'annual' | 'multi-year'>('annual');
  const [savingObj, setSavingObj] = useState(false);
  const [objError, setObjError] = useState<string | null>(null);

  // Quick-add goal form
  const [newGoalName, setNewGoalName] = useState('');
  const [newGoalDescription, setNewGoalDescription] = useState('');
  const [newGoalPriority, setNewGoalPriority] = useState<'critical' | 'high' | 'medium' | 'low'>('medium');
  const [newGoalTimeHorizon, setNewGoalTimeHorizon] = useState<'quarterly' | 'annual' | 'multi-year'>('annual');
  const [newGoalObjectiveId, setNewGoalObjectiveId] = useState('');
  const [savingGoal, setSavingGoal] = useState(false);
  const [goalError, setGoalError] = useState<string | null>(null);

  if (isCreating) return <StrategyCreateForm />;

  if (!strategy) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        <p className="text-[var(--text-ter)]">Strategy not found</p>
        <Link to="/prioritization/strategy/list" className="text-[var(--text-ter)] hover:text-[var(--text-ter)]">
          Back to strategies
        </Link>
      </div>
    );
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this strategy? This cannot be undone.')) return;
    const { ok, error } = await deleteStrategy(strategy.id);
    if (!ok) {
      window.alert(error ?? 'Could not delete strategy.');
      return;
    }
    navigate('/prioritization/strategy');
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) return;
    setIsSaving(true);
    try {
      updateStrategy(strategy.id, {
        name: editForm.name.trim(),
        description: editForm.description.trim() || undefined,
        timeHorizon: editForm.timeHorizon,
      });
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddObjective = async () => {
    if (!newObjName.trim() || !workspaceId) return;
    setSavingObj(true);
    setObjError(null);
    try {
      await createObjective(
        {
          name: newObjName.trim(),
          description: newObjDescription.trim(),
          strategyId: strategy.id,
          timeHorizon: newObjTimeHorizon,
          priority: newObjPriority,
          status: 'active',
          workspaceId,
          createdBy: userId,
        },
        userId
      );
      setNewObjName('');
      setNewObjDescription('');
      setNewObjPriority('medium');
      setNewObjTimeHorizon('annual');
      setShowAddObjectiveModal(false);
      await loadRelated();
    } catch (err) {
      setObjError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSavingObj(false);
    }
  };

  const handleAddGoal = async () => {
    if (!newGoalName.trim() || !workspaceId) return;
    setSavingGoal(true);
    setGoalError(null);
    try {
      await createGoal(
        {
          name: newGoalName.trim(),
          description: newGoalDescription.trim(),
          objectiveId: newGoalObjectiveId || undefined,
          strategyId: strategy.id,
          timeHorizon: newGoalTimeHorizon,
          priority: newGoalPriority,
          status: 'active',
          workspaceId,
          createdBy: userId,
        },
        userId
      );
      setNewGoalName('');
      setNewGoalDescription('');
      setNewGoalPriority('medium');
      setNewGoalTimeHorizon('annual');
      setNewGoalObjectiveId('');
      setShowAddGoalModal(false);
      await loadRelated();
    } catch (err) {
      setGoalError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSavingGoal(false);
    }
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      {/* Back nav */}
      <div className="mb-4 flex items-center justify-between">
        <Link
          to="/prioritization/strategy"
          className="inline-flex items-center text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] hover:text-[var(--text-sec)]"
        >
          <ArrowLeftIcon className="mr-1 h-4 w-4" />
          Back to Strategies
        </Link>
        <Link
          to="/prioritization/strategy/new"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          New Strategy
        </Link>
      </div>

      {/* Header — full width above columns */}
      {/* Wraps on a phone: name first, the actions below it */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-[1_1_16rem]">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{strategy.name}</h1>
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              strategy.status === 'active' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
              strategy.status === 'draft' ? 'bg-[var(--hover-tint)] text-[var(--text-pri)]' :
              'bg-[var(--coral-dim)] text-[var(--coral)]'
            }`}>
              {getStrategyStatusLabel(strategy.status)}
            </span>
          </div>
          <p className="mt-1 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            {getTimeHorizonLabel(strategy.timeHorizon)} strategy
            {strategy.ownerName && ` • Owner: ${strategy.ownerName}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StrategyConnectivityHelpButton context="strategy_detail" />
          {strategy.status === 'draft' && (
            <div className="relative group">
              <button onClick={() => activateStrategy(strategy.id)}
                className="inline-flex items-center bg-[var(--green)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--green)]">
                <CheckCircleIcon className="mr-1.5 h-4 w-4" /> Activate
              </button>
              <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-[var(--bg)] px-3 py-2 text-xs text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50">
                Mark this strategy as the active direction for your team. Active strategies appear in prioritization workflows and can have cycles linked to them.
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
              </div>
            </div>
          )}
          {strategy.status === 'active' && (
            <button onClick={() => archiveStrategy(strategy.id)}
              className="inline-flex items-center border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]">
              <ArchiveBoxIcon className="mr-1.5 h-4 w-4" /> Archive
            </button>
          )}
          <button onClick={() => setIsEditing(true)}
            className="inline-flex items-center border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]">
            <PencilIcon className="mr-1.5 h-4 w-4" /> Edit
          </button>
          <button onClick={handleDelete}
            className="inline-flex items-center border border-[var(--coral)] bg-[var(--bg-raised)] px-3 py-1.5 text-sm font-medium text-[var(--coral)] hover:bg-[var(--coral-dim)]">
            <TrashIcon className="mr-1.5 h-4 w-4" /> Delete
          </button>
        </div>
      </div>

      {/* Two-column layout from 768px; one column on a phone, the side panel after the main content */}
      <div className="flex flex-col md:flex-row gap-6 items-stretch md:items-start">

        {/* ── Left: main content ── */}
        <div className="flex-1 min-w-0 space-y-6">

          {/* Description */}
          {strategy.description && (
            <div className="border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]/40 p-5">
              <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">Description</h3>
              <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">{strategy.description}</p>
            </div>
          )}

          {/* Objectives */}
          <div className="border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]/40 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Objectives</h3>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">Measurable targets for this strategy</p>
              </div>
              <div className="flex items-center gap-3">
                <Link to={`/prioritization/strategy/objectives?strategyId=${strategy.id}`}
                  className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]">
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Full view
                </Link>
                <button onClick={openLinkObjectiveModal}
                  className="inline-flex items-center text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                  <LinkIcon className="h-3.5 w-3.5 mr-1" /> Connect
                </button>
                <button onClick={() => setShowAddObjectiveModal(true)}
                  className="inline-flex items-center text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                  <PlusIcon className="h-3.5 w-3.5 mr-1" /> Add new
                </button>
              </div>
            </div>
            {loadingRelated ? (
              <p className="text-sm text-[var(--text-ter)]">Loading…</p>
            ) : objectives.length === 0 ? (
              <div className="border border-dashed border-[var(--rule)] p-5 text-center">
                <FlagIcon className="mx-auto h-7 w-7 text-[var(--text-ter)] mb-2" />
                <p className="text-sm text-[var(--text-ter)]">No objectives linked yet</p>
                <div className="mt-2 flex items-center justify-center gap-3">
                  <button onClick={openLinkObjectiveModal} className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                    Connect existing
                  </button>
                  <span className="text-[var(--text-ter)]">·</span>
                  <button onClick={() => setShowAddObjectiveModal(true)} className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                    Create new
                  </button>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                {objectives.map(obj => (
                  <div key={obj.id} className="py-2.5 flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{obj.name}</p>
                      {obj.description && <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{obj.description}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <PriorityBadge priority={obj.priority} />
                      <span className="text-xs text-[var(--text-ter)]">{obj.timeHorizon}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Objective weights (roadmap P-9) */}
          {!loadingRelated && (
            <ObjectiveWeightsPanel strategyId={strategy.id} objectives={objectives} onSaved={() => void loadRelated()} />
          )}

          {/* Goals */}
          <div className="border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]/40 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Goals</h3>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">Specific targets nested under objectives</p>
              </div>
              <div className="flex items-center gap-3">
                <Link to={`/prioritization/strategy/objectives?strategyId=${strategy.id}&tab=goals`}
                  className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]">
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Full view
                </Link>
                <button onClick={openLinkGoalModal}
                  className="inline-flex items-center text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                  <LinkIcon className="h-3.5 w-3.5 mr-1" /> Connect
                </button>
                <button onClick={() => setShowAddGoalModal(true)}
                  className="inline-flex items-center text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                  <PlusIcon className="h-3.5 w-3.5 mr-1" /> Add new
                </button>
              </div>
            </div>
            {loadingRelated ? (
              <p className="text-sm text-[var(--text-ter)]">Loading…</p>
            ) : goals.length === 0 ? (
              <div className="border border-dashed border-[var(--rule)] p-5 text-center">
                <p className="text-sm text-[var(--text-ter)]">No goals linked yet</p>
                <div className="mt-2 flex items-center justify-center gap-3">
                  <button onClick={openLinkGoalModal} className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]">Connect existing</button>
                  <span className="text-[var(--text-ter)]">·</span>
                  <button onClick={() => setShowAddGoalModal(true)} className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]">Create new</button>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                {goals.map(goal => {
                  const parentObj = objectives.find(o => o.id === goal.objectiveId);
                  return (
                    <div key={goal.id} className="py-2.5 flex items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{goal.name}</p>
                        {parentObj && <p className="text-xs text-[var(--text-ter)] mt-0.5">under {parentObj.name}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <PriorityBadge priority={goal.priority} />
                        <span className="text-xs text-[var(--text-ter)]">{goal.timeHorizon}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>{/* end left column */}

        {/* ── Right: narrow sticky panel ── */}
        <div className="w-full md:w-72 flex-shrink-0 space-y-4 md:sticky md:top-6 md:self-start">

          {/* Outcome Drivers */}
          <LinkedOutcomeDriversPanel
            strategyId={strategyId!}
            linkedDrivers={linkedDrivers}
            allDrivers={allDrivers}
            onLink={(driverId) => updateDriver(driverId, { strategyId: strategyId! })}
            onUnlink={(driverId) => updateDriver(driverId, { strategyId: null })}
          />

          {/* Linked Cycles */}
          <LinkedCyclesPanel
            linkedCycleIds={strategy.linkedCycleIds}
            cycles={cycles}
            onLink={(cycleId) => linkCyclesToStrategy(strategy.id, [cycleId])}
            onUnlink={(cycleId) => unlinkCycleFromStrategy(strategy.id, cycleId)}
          />

          {/* Strategy Canvases */}
          <LinkedStrategyCanvasesPanel strategyId={strategy.id} />

        </div>{/* end right panel */}
      </div>{/* end two-column layout */}

      {/* ================================================================
          EDIT STRATEGY MODAL
      ================================================================ */}
      {isEditing && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-[var(--surface)] bg-opacity-75" onClick={() => setIsEditing(false)} />
            <div className="relative bg-[var(--bg-raised)] shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-[var(--text-pri)]">Edit Strategy</h3>
                <button onClick={() => setIsEditing(false)} className="text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Strategy Name <span className="text-[var(--coral)]">*</span>
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-[var(--slate)] text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-[var(--slate)] text-sm resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Time Horizon</label>
                  <select
                    value={editForm.timeHorizon}
                    onChange={(e) => setEditForm({ ...editForm, timeHorizon: e.target.value as StrategyTimeHorizon })}
                    className="bg-[var(--bg-raised)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-[var(--slate)] text-sm"
                  >
                    <option value="quarterly">Quarterly</option>
                    <option value="annual">Annual</option>
                    <option value="multi-year">Multi-Year</option>
                  </select>
                </div>

              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={!editForm.name.trim() || isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================
          ADD OBJECTIVE MODAL
      ================================================================ */}
      {showAddObjectiveModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-[var(--surface)] bg-opacity-75" onClick={() => setShowAddObjectiveModal(false)} />
            <div className="relative bg-[var(--bg-raised)] shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[var(--text-pri)]">Add Objective</h3>
                <button onClick={() => setShowAddObjectiveModal(false)} className="text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Name <span className="text-[var(--coral)]">*</span>
                  </label>
                  <input
                    type="text"
                    value={newObjName}
                    onChange={(e) => setNewObjName(e.target.value)}
                    autoFocus
                    placeholder="e.g., Increase customer retention"
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-[var(--slate)] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description</label>
                  <textarea
                    value={newObjDescription}
                    onChange={(e) => setNewObjDescription(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-[var(--slate)] text-sm resize-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Priority</label>
                    <select
                      value={newObjPriority}
                      onChange={(e) => setNewObjPriority(e.target.value as typeof newObjPriority)}
                      className="bg-[var(--bg-raised)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Time Horizon</label>
                    <select
                      value={newObjTimeHorizon}
                      onChange={(e) => setNewObjTimeHorizon(e.target.value as typeof newObjTimeHorizon)}
                      className="bg-[var(--bg-raised)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="quarterly">Quarterly</option>
                      <option value="annual">Annual</option>
                      <option value="multi-year">Multi-Year</option>
                    </select>
                  </div>
                </div>
                {objError && <p className="text-sm text-[var(--coral)]">{objError}</p>}
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowAddObjectiveModal(false)}
                  className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddObjective}
                  disabled={!newObjName.trim() || savingObj}
                  className="px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingObj ? 'Saving…' : 'Add Objective'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================
          ADD GOAL MODAL
      ================================================================ */}
      {showAddGoalModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-[var(--surface)] bg-opacity-75" onClick={() => setShowAddGoalModal(false)} />
            <div className="relative bg-[var(--bg-raised)] shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[var(--text-pri)]">Add Goal</h3>
                <button onClick={() => setShowAddGoalModal(false)} className="text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Name <span className="text-[var(--coral)]">*</span>
                  </label>
                  <input
                    type="text"
                    value={newGoalName}
                    onChange={(e) => setNewGoalName(e.target.value)}
                    autoFocus
                    placeholder="e.g., Reduce churn by 10% in Q2"
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-[var(--slate)] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description</label>
                  <textarea
                    value={newGoalDescription}
                    onChange={(e) => setNewGoalDescription(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-[var(--slate)] text-sm resize-none"
                  />
                </div>
                {objectives.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                      Parent Objective (optional)
                    </label>
                    <select
                      value={newGoalObjectiveId}
                      onChange={(e) => setNewGoalObjectiveId(e.target.value)}
                      className="bg-[var(--bg-raised)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="">No parent objective</option>
                      {objectives.map(o => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Priority</label>
                    <select
                      value={newGoalPriority}
                      onChange={(e) => setNewGoalPriority(e.target.value as typeof newGoalPriority)}
                      className="bg-[var(--bg-raised)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Time Horizon</label>
                    <select
                      value={newGoalTimeHorizon}
                      onChange={(e) => setNewGoalTimeHorizon(e.target.value as typeof newGoalTimeHorizon)}
                      className="bg-[var(--bg-raised)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="quarterly">Quarterly</option>
                      <option value="annual">Annual</option>
                      <option value="multi-year">Multi-Year</option>
                    </select>
                  </div>
                </div>
                {goalError && <p className="text-sm text-[var(--coral)]">{goalError}</p>}
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowAddGoalModal(false)}
                  className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddGoal}
                  disabled={!newGoalName.trim() || savingGoal}
                  className="px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingGoal ? 'Saving…' : 'Add Goal'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================
          LINK EXISTING OBJECTIVE MODAL
      ================================================================ */}
      {showLinkObjectiveModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-[var(--surface)] bg-opacity-75" onClick={() => setShowLinkObjectiveModal(false)} />
            <div className="relative bg-[var(--bg-raised)] shadow-xl max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-[var(--text-pri)]">Connect Objective</h3>
                  <p className="text-sm text-[var(--text-ter)] mt-0.5">Link an existing objective to this strategy</p>
                </div>
                <button onClick={() => setShowLinkObjectiveModal(false)} className="text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {loadingAll ? (
                <p className="py-8 text-center text-sm text-[var(--text-ter)]">Loading objectives…</p>
              ) : (() => {
                const linkedIds = new Set(objectives.map(o => o.id));
                const available = allObjectives.filter(o => !linkedIds.has(o.id));
                return available.length === 0 ? (
                  <p className="py-8 text-center text-sm text-[var(--text-ter)]">
                    No unlinked objectives found.{' '}
                    <button onClick={() => { setShowLinkObjectiveModal(false); setShowAddObjectiveModal(true); }} className="text-[var(--text-ter)] hover:underline">
                      Create a new one instead.
                    </button>
                  </p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {available.map(obj => (
                      <button
                        key={obj.id}
                        onClick={() => handleLinkObjective(obj.id)}
                        disabled={linking}
                        className="w-full text-left px-4 py-3 border border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors disabled:opacity-50"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[var(--text-pri)] truncate">{obj.name}</p>
                            {obj.description && (
                              <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{obj.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <PriorityBadge priority={obj.priority} />
                            <span className="text-xs text-[var(--text-ter)]">{obj.timeHorizon}</span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })()}

              <div className="mt-5 flex justify-end">
                <button
                  onClick={() => setShowLinkObjectiveModal(false)}
                  className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================
          LINK EXISTING GOAL MODAL
      ================================================================ */}
      {showLinkGoalModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-[var(--surface)] bg-opacity-75" onClick={() => setShowLinkGoalModal(false)} />
            <div className="relative bg-[var(--bg-raised)] shadow-xl max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-[var(--text-pri)]">Connect Goal</h3>
                  <p className="text-sm text-[var(--text-ter)] mt-0.5">Link an existing goal to this strategy</p>
                </div>
                <button onClick={() => setShowLinkGoalModal(false)} className="text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {loadingAll ? (
                <p className="py-8 text-center text-sm text-[var(--text-ter)]">Loading goals…</p>
              ) : (() => {
                const linkedIds = new Set(goals.map(g => g.id));
                const available = allGoals.filter(g => !linkedIds.has(g.id));
                return available.length === 0 ? (
                  <p className="py-8 text-center text-sm text-[var(--text-ter)]">
                    No unlinked goals found.{' '}
                    <button onClick={() => { setShowLinkGoalModal(false); setShowAddGoalModal(true); }} className="text-[var(--text-ter)] hover:underline">
                      Create a new one instead.
                    </button>
                  </p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {available.map(goal => (
                      <button
                        key={goal.id}
                        onClick={() => handleLinkGoal(goal.id)}
                        disabled={linking}
                        className="w-full text-left px-4 py-3 border border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors disabled:opacity-50"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[var(--text-pri)] truncate">{goal.name}</p>
                            {goal.description && (
                              <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{goal.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <PriorityBadge priority={goal.priority} />
                            <span className="text-xs text-[var(--text-ter)]">{goal.timeHorizon}</span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })()}

              <div className="mt-5 flex justify-end">
                <button
                  onClick={() => setShowLinkGoalModal(false)}
                  className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default StrategyDetail;
